import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';
import { Calculator } from 'react-bootstrap-icons';
import { Table, Form, Button, InputGroup } from 'react-bootstrap';
import { 
  GetSelectionGroupItemsBySelectionGroupId, 
  UpdateSelectionGroupItemPriceModifier, 
  UpdateSelectionGroupItemMinValue, 
  UpdateSelectionGroupItemMaxValue,
  getSelectionGroupItemThirdPartyPrices,
  setSelectionGroupItemThirdPartyPrice,
  deleteSelectionGroupItemThirdPartyPrice
} from '../../../../../../../../crud/menu_api/selection_groups';
import { GetAllThirdPartyProviders } from '../../../../../../../../crud/configuration_api/third_party_providers';
import DeleteItemFromGroupModal from './components/DeleteItemFromGroupModal/DeleteItemFromGroupModal';
import './GroupItemsTable.scss';

interface SelectionGroupType {
  readonly selection_group_id: number;
  selection_group_name: string;
  selection_group_description?: string;
  local_id: number;
}

interface SelectionItemType {
  selection_group_id: number;
  item_id: number;
  label: string;
  item_name: string;
  desk_price_modifier: number;
  delivery_price_modifier: number;
  online_price_modifier: number;
  price_modifier: number;
  min_selections: number;
  max_selections: number;
}

interface TableProps {
  sg: SelectionGroupType;
  items: SelectionItemType[];
  setItems: (items: SelectionItemType[]) => void;
  onItemRemoved?: (itemId: number) => void; // Nueva prop para notificar al padre
}

const GroupItemsTable: React.FC<TableProps> = ({
  sg,
  items,
  setItems,
  onItemRemoved
}) => {
  const [itemBulkValues, setItemBulkValues] = useState<{[key: number]: number}>({});
  const inputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SelectionItemType | null>(null);
  const [loadingItems, setLoadingItems] = useState(true);
  const [thirdPartyProviders, setThirdPartyProviders] = useState<{ third_party_provider_id: number; name: string }[]>([]);
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [tppPrices, setTppPrices] = useState<{ [itemId: number]: { [providerId: number]: string } }>({});
  const [tppInitialPrices, setTppInitialPrices] = useState<{ [itemId: number]: { [providerId: number]: string } }>({});

  // Fetch data once when component mounts
  useEffect(() => {
    setLoadingItems(true);
    GetSelectionGroupItemsBySelectionGroupId(sg.selection_group_id)
      .then((response) => { 
        setItems(response.data); 
      })
      .catch((error) => {
        console.error('Error fetching selection group items', error);
        toast.error('Error fetching selection group items');
      })
      .finally(() => {
        setLoadingItems(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once

  // Fetch third-party providers list once
  useEffect(() => {
    GetAllThirdPartyProviders()
      .then((response) => {
        if (response?.data) {
          setThirdPartyProviders(response.data);
        }
      })
      .catch((err) => console.error('Error fetching third party providers:', err));
  }, []);

  const handleToggleProviderPrices = (itemId: number) => {
    if (expandedItemId === itemId) {
      setExpandedItemId(null);
      return;
    }
    setExpandedItemId(itemId);
    
    getSelectionGroupItemThirdPartyPrices(sg.selection_group_id, itemId)
      .then((response: any) => {
        if (response?.data?.prices) {
          const pricesMap: { [providerId: number]: string } = {};
          response.data.prices.forEach((p: any) => {
            pricesMap[p.third_party_provider_id] = p.price_modifier.toString();
          });
          setTppPrices(prev => ({ ...prev, [itemId]: pricesMap }));
          setTppInitialPrices(prev => ({ ...prev, [itemId]: { ...pricesMap } }));
        }
      })
      .catch((err) => console.error('Error fetching SG item TPP prices:', err));
  };

  const handleTppPriceChange = (itemId: number, providerId: number, value: string) => {
    setTppPrices(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [providerId]: value
      }
    }));
  };

  const handleTppPriceBlur = (itemId: number, providerId: number) => {
    const price = tppPrices[itemId]?.[providerId];
    const initialPrice = tppInitialPrices[itemId]?.[providerId];
    
    if (price === initialPrice) return;
    
    if (price && parseFloat(price) > 0) {
      setSelectionGroupItemThirdPartyPrice(sg.selection_group_id, itemId, providerId, parseFloat(price))
        .then(() => {
          setTppInitialPrices(prev => ({
            ...prev,
            [itemId]: { ...(prev[itemId] || {}), [providerId]: price }
          }));
          toast.success('Precio de proveedor actualizado');
        })
        .catch(() => toast.error('Error actualizando precio de proveedor'));
    } else if (!price || parseFloat(price) === 0) {
      if (initialPrice) {
        deleteSelectionGroupItemThirdPartyPrice(sg.selection_group_id, itemId, providerId)
          .then(() => {
            setTppInitialPrices(prev => {
              const next = { ...prev };
              if (next[itemId]) delete next[itemId][providerId];
              return next;
            });
            toast.success('Precio de proveedor eliminado');
          })
          .catch(() => toast.error('Error eliminando precio de proveedor'));
      }
    }
  };

  const handleSetAllPricesForItem = (itemId: number) => {
    const value = itemBulkValues[itemId] || 0;
    
    if (value < 0) {
      toast.error('El precio no puede ser negativo');
      return;
    }
    // First, update the local state immediately for responsive UI
    const updatedItems = items.map((item) => {
      if (item.item_id === itemId) {
        return {
          ...item,
          price_modifier: value,
          delivery_price_modifier: value,
          online_price_modifier: value,
          desk_price_modifier: value
        };
      }
      return item;
    });
    setItems(updatedItems);
    
    // Then sync with backend
    const fields = ['price_modifier', 'delivery_price_modifier', 'online_price_modifier', 'desk_price_modifier'];
    const priceTypes = ['restaurant', 'delivery', 'online', 'desk'];
    
    // Use Promise.all to handle all updates simultaneously
    const updatePromises = fields.map((field, index) => {
      return UpdateSelectionGroupItemPriceModifier(sg.selection_group_id, itemId, priceTypes[index], value)
        .catch((error) => {
          console.error(`Error updating ${field}`, error);
          toast.error(`Error al actualizar ${priceTypes[index]}`);
          throw error;
        });
    });
    
    Promise.all(updatePromises)
      .then(() => {
        toast.success(`Todos los precios establecidos en ${value}`);
      })
      .catch(() => {
        // If any update fails, revert the local state by refetching from backend
        GetSelectionGroupItemsBySelectionGroupId(sg.selection_group_id)
          .then((response) => {
            setItems(response.data);
            toast.error('Algunos precios no se pudieron actualizar. Se han revertido los cambios.');
          })
          .catch((fetchError) => {
            console.error('Error refetching data after failed update', fetchError);
            toast.error('Error al revertir los cambios');
          });
      });
  };

  const updateItemBulkValue = (itemId: number, value: number) => {
    setItemBulkValues(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const handleLocalUpdate = (itemId: number, field: string, value: number) => {
    // Only update local state for responsive UI
    const updatedItems = items.map((item) => {
      if (item.item_id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setItems(updatedItems);
  };

  const handleConfirmUpdate = (itemId: number, field: string, value: number) => {
    // First update local state immediately for responsive UI
    const updatedItems = items.map((item) => {
      if (item.item_id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setItems(updatedItems);
    
    // Then sync with backend
    let price_type = '';
    switch (field) {
      case 'price_modifier':
        price_type = 'restaurant';
        break;
      case 'delivery_price_modifier':
        price_type = 'delivery';
        break;
      case 'online_price_modifier':
        price_type = 'online';
        break;
      case 'desk_price_modifier':
        price_type = 'desk';
        break;
      default:
        if (field === 'min_selections') {
          UpdateSelectionGroupItemMinValue(sg.selection_group_id, itemId, value)
            .then(() => {
              toast.success(`Selecciones mínimas actualizadas: ${value}`);
            })
            .catch((error) => {
              console.error('Error updating min value', error);
              toast.error('Error actualizando el valor mínimo');
              // Revert local state on error
              GetSelectionGroupItemsBySelectionGroupId(sg.selection_group_id)
                .then((response) => setItems(response.data))
                .catch((fetchError) => console.error('Error refetching data', fetchError));
            });
          return;
        }
        if (field === 'max_selections') {
          UpdateSelectionGroupItemMaxValue(sg.selection_group_id, itemId, value)
            .then(() => {
              toast.success(`Selecciones máximas actualizadas: ${value}`);
            })
            .catch((error) => {
              console.error('Error updating max value', error);
              toast.error('Error actualizando el valor máximo');
              // Revert local state on error
              GetSelectionGroupItemsBySelectionGroupId(sg.selection_group_id)
                .then((response) => setItems(response.data))
                .catch((fetchError) => console.error('Error refetching data', fetchError));
            });
          return;
        }
        toast.error('Tipo de campo inválido');
        return;
    }

    UpdateSelectionGroupItemPriceModifier(sg.selection_group_id, itemId, price_type, value)
      .then(() => {
        const priceTypeNames: {[key: string]: string} = {
          'restaurant': 'Restaurante',
          'delivery': 'Delivery',
          'online': 'Online',
          'desk': 'Mesa'
        };
        toast.success(`Precio ${priceTypeNames[price_type]} actualizado: ${value}`);
      })
      .catch((error) => {
        console.error('Error updating price modifier', error);
        toast.error('Error al actualizar el modificador de precio');
        // Revert local state on error
        GetSelectionGroupItemsBySelectionGroupId(sg.selection_group_id)
          .then((response) => setItems(response.data))
          .catch((fetchError) => console.error('Error refetching data', fetchError));
      });
  };

  const handleDeleteItemFromGroup = (item: SelectionItemType) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const renderPriceInput = (value: number, itemId: number, field: string, label: string) => (
    <Form.Control
      ref={(ref: HTMLInputElement | null) => {
        inputRefs.current[`${field}_${itemId}`] = ref;
      }}
      type="text"
      value={value}
      onChange={(e) => handleLocalUpdate(itemId, field, Number(e.target.value))}
      onBlur={(e) => handleConfirmUpdate(itemId, field, Number(e.target.value))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleConfirmUpdate(itemId, field, Number((e.target as HTMLInputElement).value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="price-input"
      title={`Precio para ${label} - Presiona Enter o sal del campo para guardar`}
      placeholder="0.00"
    />
  );

  const renderSelectionInput = (value: number, itemId: number, field: string, type: 'min' | 'max') => (
    <div className="selection-input-container">
      <Form.Control
        type="number"
        min="0"
        value={value}
        onChange={(e) => handleLocalUpdate(itemId, field, Number(e.target.value))}
        onBlur={(e) => handleConfirmUpdate(itemId, field, Number(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleConfirmUpdate(itemId, field, Number((e.target as HTMLInputElement).value));
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="selection-input"
        title={`Selecciones ${type === 'min' ? 'mínimas' : 'máximas'} - Presiona Enter o sal del campo para guardar`}
      />
      <div className="selection-arrows">
        <button 
          type="button"
          className="arrow-btn arrow-up"
          onClick={() => {
            handleLocalUpdate(itemId, field, value + 1);
            handleConfirmUpdate(itemId, field, value + 1);
          }}
        >
          ▲
        </button>
        <button 
          type="button"
          className="arrow-btn arrow-down"
          onClick={() => {
            const newValue = Math.max(0, value - 1);
            handleLocalUpdate(itemId, field, newValue);
            handleConfirmUpdate(itemId, field, newValue);
          }}
        >
          ▼
        </button>
      </div>
    </div>
  );

  // Show loading spinner while fetching
  if (loadingItems) {
    return (
      <div className="loading-container">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <p>Cargando productos del grupo...</p>
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <>
      <Table responsive className="Groups-items-configuration-table">
          <thead>
            <tr>
              <th className="item-name-col">
                Item
              </th>
              <th className="bulk-control-col">
                Establecer Precios
              </th>
              <th className="price-col">
                Restaurante
              </th>
              <th className="price-col">
                Delivery
              </th>
              <th className="price-col">
                Online
              </th>
              <th className="price-col">
                Mesa
              </th>
              <th className="selection-col">
                Mín
              </th>
              <th className="selection-col">
                Máx
              </th>
              <th className="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: SelectionItemType, index) => (
              <React.Fragment key={item.item_id}>
              <tr className={`item-row ${index % 2 === 0 ? 'even' : 'odd'}`}>
                <td className="item-name-cell">
                  <div className="item-info">
                    <strong className="item-name">{item.label}</strong>
                  </div>
                </td>
                <td className="bulk-control-cell">
                  <InputGroup size="sm" className="bulk-price-control">
                    <Form.Control
                      type="text"
                      value={itemBulkValues[item.item_id] || ''}
                      onChange={(e) => updateItemBulkValue(item.item_id, parseFloat(e.target.value) || 0)}
                      placeholder="Precio"
                      className="bulk-price-input"
                    />
                    <Button
                      variant="primary"
                      onClick={() => handleSetAllPricesForItem(item.item_id)}
                      className="bulk-apply-btn"
                      title={`Establecer ${itemBulkValues[item.item_id] || 0} en todos los precios`}
                    >
                      <Calculator size={12} />
                    </Button>
                  </InputGroup>
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.price_modifier, item.item_id, 'price_modifier', 'Restaurante')}
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.delivery_price_modifier, item.item_id, 'delivery_price_modifier', 'Delivery')}
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.online_price_modifier, item.item_id, 'online_price_modifier', 'Online')}
                </td>
                <td className="price-cell">
                  {renderPriceInput(item.desk_price_modifier, item.item_id, 'desk_price_modifier', 'Mesa')}
                </td>
                <td className="selection-cell">
                  {renderSelectionInput(item.min_selections, item.item_id, 'min_selections', 'min')}
                </td>
                <td className="selection-cell">
                  {renderSelectionInput(item.max_selections, item.item_id, 'max_selections', 'max')}
                </td>
                <td className="actions-cell">
                  {thirdPartyProviders.length > 0 && (
                    <Button
                      variant={expandedItemId === item.item_id ? "outline-primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => handleToggleProviderPrices(item.item_id)}
                      className="expand-tpp-btn"
                      type="button"
                      title="Precios por aplicación de delivery"
                      style={{ marginRight: '4px' }}
                    >
                      {expandedItemId === item.item_id ? '▼' : '▶'}
                    </Button>
                  )}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteItemFromGroup(item)}
                    className="delete-item-btn"
                    type="button"
                    title={`Eliminar ${item.item_name}`}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
              {expandedItemId === item.item_id && thirdPartyProviders.length > 0 && (
                <tr className="tpp-expanded-row">
                  <td colSpan={9}>
                    <div className="tpp-prices-container">
                      <span className="tpp-label">Precios por Aplicación de Delivery:</span>
                      <span className="tpp-help">(Vacío = usa precio Delivery)</span>
                      <div className="tpp-prices-grid">
                        {thirdPartyProviders.map((provider) => (
                          <div key={provider.third_party_provider_id} className="tpp-price-item">
                            <span className="tpp-provider-name">{provider.name}</span>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={tppPrices[item.item_id]?.[provider.third_party_provider_id] || ''}
                              placeholder={item.delivery_price_modifier?.toString() || '0.00'}
                              onChange={(e) => handleTppPriceChange(item.item_id, provider.third_party_provider_id, e.target.value)}
                              onBlur={() => handleTppPriceBlur(item.item_id, provider.third_party_provider_id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleTppPriceBlur(item.item_id, provider.third_party_provider_id);
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              className="tpp-price-input"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      
      {(showDeleteModal && sg && selectedItem) && (
        <DeleteItemFromGroupModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          sg={sg}
          item={selectedItem}
          setGroupItems={setItems}
          groupItems={items}
          onItemRemoved={onItemRemoved}
        />
      )}
    </>
  );
};

export default GroupItemsTable;
